/*****************************************************************
 *  ZOHO BRIDGE PANEL — TALKING TO THE SERVER                      *
 *  File: apps/web/src/pages/bridge/bridgeApi.js                   *
 *  What it does: every call the panel makes to                    *
 *  /api/bridge/zoho, in one place. Uses the suite's shared        *
 *  authFetch (cookie login + one silent refresh).                 *
 *  Every function answers { ok, status, data, message } and       *
 *  never throws, so a screen can always say what happened.        *
 *  Reads: the bridge API.   Writes: the bridge API (decisions,    *
 *  keys, relinks) — only when a person pressed a button.          *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE ONE CALLER (and the address-bar helper)       *
 *  SECTION 2 — THE NAMED CALLS                                    *
 *****************************************************************/

import { API, authFetch } from '../../lib/api';

// Where the bridge door lives under the API.
const BRIDGE_PATH = '/bridge/zoho';

/*****************************************************************
 *  SECTION 1 — THE ONE CALLER                                     *
 *****************************************************************/

/* ---- callBridge(method, subPath, body) -------------------------
   Calls the bridge door and reads the suite's envelope.
   In: GET/POST; the path after /bridge/zoho; an optional body.
   Out: { ok, status, data, message }. Never throws.             */
async function callBridge(method, subPath, body) {
  try {
    const fetchOptions = { method };

    if (body !== undefined) {
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await authFetch(API + BRIDGE_PATH + subPath, fetchOptions);
    let envelope = null;

    try {
      envelope = await response.json();
    } catch (parseError) {
      envelope = null;
    }

    const hasEnvelope = Boolean(envelope) && typeof envelope === 'object';
    const data = hasEnvelope && envelope.data !== undefined ? envelope.data : null;
    let message = '';

    if (hasEnvelope && envelope.error) {
      message = typeof envelope.error === 'string' ? envelope.error : envelope.error.message || '';
    }

    return {
      ok: response.ok,
      status: response.status,
      data,
      message,
    };
  } catch (networkError) {
    return {
      ok: false,
      status: 0,
      data: null,
      message: 'Could not reach the server. Check your connection.',
    };
  }
}

/* ---- buildQuery(values) ----------------------------------------
   "?a=1&b=2" from an object, leaving blanks out.
   In: { name: value }.  Out: text (starts with "?").            */
function buildQuery(values) {
  const queryParts = new URLSearchParams();

  for (const queryName of Object.keys(values || {})) {
    const queryValue = values[queryName];
    const hasValue = queryValue !== undefined && queryValue !== null && queryValue !== '';

    if (hasValue) {
      queryParts.set(queryName, String(queryValue));
    }
  }

  return '?' + queryParts.toString();
}

/*****************************************************************
 *  SECTION 2 — THE NAMED CALLS                                    *
 *****************************************************************/

/* ---- the holding pile ---- */
export function fetchSummary() {
  return callBridge('GET', '/summary');
}

/* ---- fetchPile({ status, kind, badge, search, page, pageSize })
   One page: { rows, total, page, pageSize } (defect 372). ---- */
export function fetchPile(pileFilters) {
  return callBridge('GET', '/inbox' + buildQuery(pileFilters));
}

/* ---- approveRecord(inboxId, why, allowOlder, choice)
   choice: { chooseCustomerId } or { createNew: true } (defect 379). ---- */
export function approveRecord(inboxId, why, allowOlder, choice) {
  const approveBody = {
    why,
    allowOlder: Boolean(allowOlder),
    ...(choice || {}),
  };

  return callBridge('POST', '/inbox/' + inboxId + '/approve', approveBody);
}

/* ---- approve all that match (phase 2b) ---- */
export function fetchApproveAllCount(pileFilters) {
  return callBridge('GET', '/inbox/approve-all/count' + buildQuery(pileFilters));
}

export function startApproveAll(pileFilters, expectedCount, why) {
  return callBridge('POST', '/inbox/approve-all', { ...pileFilters, expectedCount, why });
}

export function fetchApproveAllRun() {
  return callBridge('GET', '/inbox/approve-all/run');
}

export function stopApproveAll() {
  return callBridge('POST', '/inbox/approve-all/stop', {});
}

/* ---- the Zoho field pack in Customer Butler (phase 2b) ---- */
export function fetchFieldPack() {
  return callBridge('GET', '/field-pack');
}

export function setUpFieldPack(why) {
  return callBridge('POST', '/field-pack', { why });
}

export function rejectRecord(inboxId, why) {
  return callBridge('POST', '/inbox/' + inboxId + '/reject', { why });
}

export function approveManyRecords(inboxIds, why) {
  return callBridge('POST', '/inbox/approve-many', { ids: inboxIds, why });
}

/* ---- visits and technicians (phase 2a, 2026-10-13) ---- */
// The biggest page the door gives (phase 2b's paging); a trial week is 10-20 a day.
const VISITS_PAGE_SIZE = 200;

export function fetchVisits(statusFilter) {
  return fetchPile({ status: statusFilter, kind: 'visit', page: 1, pageSize: VISITS_PAGE_SIZE });
}

export function fetchVisitSettings() {
  return callBridge('GET', '/visits/settings');
}

export function fetchTechs(includeOld) {
  return callBridge('GET', '/techs' + (includeOld ? '?all=1' : ''));
}

export function confirmTech(zohoUserId, employeeId, why) {
  return callBridge('POST', '/techs/' + encodeURIComponent(zohoUserId) + '/confirm', { employeeId, why });
}

export function unmatchTech(zohoUserId, why) {
  return callBridge('POST', '/techs/' + encodeURIComponent(zohoUserId) + '/unmatch', { why });
}

export function suggestTechAgain(zohoUserId) {
  return callBridge('POST', '/techs/' + encodeURIComponent(zohoUserId) + '/suggest', { why: 'Asked the roster again' });
}

/* ---- prices ---- */
export function fetchPrices(pageNumber) {
  return callBridge('GET', '/prices' + buildQuery({ page: pageNumber }));
}

export function fetchPriceHistory(zohoItemId) {
  return callBridge('GET', '/prices/' + encodeURIComponent(zohoItemId) + '/history');
}

/* ---- customer links ---- */
export function fetchLinks(includeOld, pageNumber) {
  return callBridge('GET', '/links' + buildQuery({ all: includeOld ? '1' : '', page: pageNumber }));
}

export function relinkCustomer(linkId, customerId, why) {
  return callBridge('POST', '/links/' + linkId + '/relink', { customerId, why });
}

/* ---- Customer Butler's own search (its door, its rules) ---- */
export async function searchButlerCustomers(searchText) {
  try {
    const response = await authFetch(API + '/customer/customers?search=' + encodeURIComponent(searchText));
    const envelope = await response.json();
    const rows = envelope && Array.isArray(envelope.data) ? envelope.data : [];

    return { ok: response.ok, status: response.status, data: rows, message: '' };
  } catch (searchError) {
    return { ok: false, status: 0, data: [], message: 'Could not search customers.' };
  }
}

/* ---- the log ---- */
export function fetchLog(pageNumber) {
  return callBridge('GET', '/log' + buildQuery({ page: pageNumber }));
}

/* ---- machine keys ---- */
export function fetchKeys() {
  return callBridge('GET', '/keys');
}

export function makeKey(keyName) {
  return callBridge('POST', '/keys', { name: keyName });
}

export function revokeKey(keyId, why) {
  return callBridge('POST', '/keys/' + keyId + '/revoke', { why });
}
